import { useCallback, useEffect, useState } from "react";

const readParam = (name) => {
	try {
		return new URLSearchParams(window.location.search).get(name);
	} catch {
		return null;
	}
};

// The current URL with `name` set to `value` (or removed when null), as a
// relative href, so a filter can render as a real link.
export const hrefWithParam = (name, value) => {
	try {
		const url = new URL(window.location.href);
		if (value) url.searchParams.set(name, value);
		else url.searchParams.delete(name);
		return `${url.pathname}${url.search}${url.hash}`;
	} catch {
		return undefined;
	}
};

// A single query-string value as React state, so a filtered view can be
// bookmarked or shared. Writes use replaceState (no history entry per click)
// and leave every other parameter untouched; a null value removes the key.
export const useUrlParam = (name) => {
	const [value, setValue] = useState(() => readParam(name));

	useEffect(() => {
		const onPopState = () => setValue(readParam(name));
		window.addEventListener("popstate", onPopState);
		return () => window.removeEventListener("popstate", onPopState);
	}, [name]);

	const update = useCallback(
		(next) => {
			setValue(next ?? null);
			try {
				const url = new URL(window.location.href);
				if (next) url.searchParams.set(name, next);
				else url.searchParams.delete(name);
				window.history.replaceState(window.history.state, "", url);
			} catch {
				// Keep the in-memory value when the URL cannot be updated.
			}
		},
		[name],
	);

	return [value, update];
};
